<template>
  <div class="h-full">
    <div class="play-as-team-setup__header">
      <div
        @click="toggleQuizTeamSetUpVisibleAction()"
        class="cursor-pointer"
      >
        <aha-icon name="arrow-left" />
      </div>
      <p class="text-sm font-bold text-secondary-100">
        {{ $i18nPresenter.t('Team set up') }}
      </p>
      <aha-icon
        name="close"
        width="16"
        height="16"
        viewBox="0 0 24 24"
        class="text-base-70 hover:text-primary-90 cursor-pointer"
        @click="toggleQuizTeamSetUpVisibleAction()"
      />
    </div>
    <div class="play-as-team-setup__content-wrapper">
      <AhaInfoBox class="mb-3">
        {{
          $i18nPresenter.t('Let your participants play quiz and be ranked as team instead of being ranked individually')
        }}
      </AhaInfoBox>

      <div class="number-of-teams__wrapper">
        <div>
          <p class="mb-3 text-sm font-bold text-secondary-100">
            {{ $i18nPresenter.t('Number of teams') }}
          </p>
          <div class="w-32 sm:w-36">
            <aha-input
              type="number"
              v-model="teamCount"
              @input="onChangeNumberOfTeam"
              :class="{
                'ring-1 ring-red-50 outline-none': teamCount < 2 || teamCount > 500
              }"
              onkeydown="
                  return event.keyCode !== 69
                  && event.keyCode !== 187
                  && event.keyCode !== 189
                  && event.keyCode !== 190"
            />
          </div>
        </div>

        <div class="mb-4">
          <p class="mb-3 text-sm font-bold text-secondary-100">
            {{ $i18nPresenter.t('Team size') }}
          </p>
          <div class="w-32 sm:w-36">
            <aha-input
              type="number"
              v-model="teamSize"
              @input="onChangeTeamSize"
              :class="{'ring-1 ring-red-50 outline-none': teamSize < 2 || teamSize > limit}"
              onkeydown="
                  return event.keyCode !== 69
                  && event.keyCode !== 187
                  && event.keyCode !== 189
                  && event.keyCode !== 190"
            />
          </div>
        </div>
      </div>

      <p class="mb-1 text-sm font-bold text-secondary-100">
        {{ $i18nPresenter.t('Create teams') }}
      </p>

      <div class="mb-2">
        <div
          v-for="(item, index) in presentation.teamData.filter(i => i.visible)"
          :key="index"
        >
          <aha-input-group class="relative">
            <div class="absolute flex items-center left-2 top-2.5">
              <span class="text-center min-w-9">{{ index + 1 }}</span>
              <span
                class="block w-5 h-5 rounded bg-base-100"
                :style="{'background-color': getColor(item) }"
              />
            </div>
            <div
              v-if="!item.edit"
              class="flex items-center ml-20 cursor-pointer item-name"
              @click="openEditName(item)"
            >
              <p class="text-sm break-word">
                {{ item.name }}
              </p>
              <aha-icon
                class="ml-1 edit-icon"
                name="edit"
                viewBox="0 0 24 24"
                width="14"
                height="14"
              />
            </div>

            <div v-if="item.edit" class="w-full">
              <div>
                <input
                  type="text"
                  class="aha-input-custom"
                  id="team-name-input"
                  v-model="item.editName"
                  :class="{error: item.error || item.editName.length > 20}"
                  @input="onChangeTeamName"
                  @keyup.13="changeTeamName(item)"
                  @blur="_handleOutFocusTeamName(item)"
                  :placeholder="$i18nPresenter.t('Team Name')"
                  :maxlength="20"
                  autofocus
                >
                <span
                  class="absolute right-2 top-2 text-secondary-40"
                  :class="{'text-red-100': item.editName.length > 20}"
                >{{ 20 - item.editName.length }}</span>
              </div>
            </div>
          </aha-input-group>
          <div class="text-red-100 mt-1" v-if="item.error">
            {{ item.error }}
          </div>
        </div>
      </div>

      <aha-button v-bind="$props" class="mb-6" @click="addNewTeam()">
        <div class="flex items-center justify-center w-full">
          <aha-icon
            class="mr-1 text-base-0"
            name="add"
            viewBox="0 0 24 24"
            width="16"
            height="16"
          />
          <span>{{ $i18nPresenter.t('Add new team') }}</span>
        </div>
      </aha-button>

      <div class="border-b border-base-30" />

      <p class="mt-5 mb-2 text-sm font-bold text-secondary-100">
        {{ $i18nPresenter.t('Team scoring rules') }}
      </p>

      <div class="mb-3">
        <aha-radio
          name="ruleScore"
          id="team-rule-average"
          v-model="presentation.teamScoringRule"
          value="average"
          @change="onChangeScoringRule"
          class="mb-0"
        >
          {{ $i18nPresenter.t('Average score of all members') }}
          <div
            v-tooltip="$i18nPresenter.t('Including those who answer incorrectly or do not answer (as zero point)')"
            class="text-secondary-40 cursor-pointer ml-2"
          >
            <i class="fas fa-question-circle" />
          </div>
        </aha-radio>
      </div>

      <div class="mb-3">
        <aha-radio
          name="ruleScore"
          id="team-rule-total"
          v-model="presentation.teamScoringRule"
          value="total"
          @change="onChangeScoringRule"
          class="mb-0"
        >
          {{ $i18nPresenter.t('Total score of all members') }}
          <div
            v-tooltip="$i18nPresenter.t('Note that this rule will handicap teams with fewer members')"
            class="text-secondary-40 cursor-pointer ml-2"
          >
            <i class="fas fa-question-circle" />
          </div>
        </aha-radio>
      </div>

      <div class="mb-4">
        <aha-radio
          name="ruleScore"
          id="team-rule-faster"
          v-model="presentation.teamScoringRule"
          value="fastest"
          @change="onChangeScoringRule"
          class="mb-0"
        >
          {{ $i18nPresenter.t('Fastest answer only') }}
          <div
            v-tooltip="$i18nPresenter.t('Only score the fastest answer from each team (even if it\'s incorrect)')"
            class="text-secondary-40 cursor-pointer ml-2"
          >
            <i class="fas fa-question-circle" />
          </div>
        </aha-radio>
      </div>

      <div class="border-b border-base-30" />

      <p class="mt-5 mb-2 text-sm font-bold text-secondary-100">
        {{ $i18nPresenter.t('Leaderboard') }}
      </p>

      <div class="hide-leaderboard__wrapper">
        <div class="flex items-center flex-grow group">
          <span class="pr-2 font-normal">{{ $i18nPresenter.t('Hide individual leaderboard') }}</span>
          <div
            v-tooltip="hideIndividualLeaderboardTooltip"
            class="text-secondary-40 cursor-pointer hidden group-hover:inline-block"
          >
            <i class="fas fa-question-circle" />
          </div>
        </div>
        <toggle-button
          v-model="presentation.isHideIndividualLeaderboard"
          :disabled="isDisableEditPresentation"
          :width="35"
          :height="20"
          class="mb-0"
          :color="{
            checked: '#348EF6',
            unchecked: '#D3D7E1'
          }"
          @change="onChangeHideIndividualLeaderboard()"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import _ from 'lodash'
import Constant from '@/constant'
import PresentationApi from '@/api/presentation'
import utils from '@/util'
import eventCategories from '@/constant/eventCategories'
import Socket from '@/socket'
import Config from '@/config'
import EventBus from '@/eventBus'

export default {
  data: () => ({
    teamCount: Constant.defaultQuizTeam.teamCount,
    teamSize: Constant.defaultQuizTeam.teamSize,
    limit: 1200,
  }),
  computed: {
    ...mapState({
      presentation: (state) => state.presentation,
      currentUser: (state) => state.currentUser,
      isDisableEditPresentation: (state) => state.isDisableEditPresentation,
    }),
    hideIndividualLeaderboardTooltip() {
      return this.$i18nPresenter.t("Players can't see individual scores in a team quiz")
    },
  },
  watch: {
    '$store.state.presentation.teamCount': function watchTeamCount() {
      this.teamCount = this.$store.state.presentation.teamCount
    },
  },
  created() {
    this.teamCount = this.$store.state.presentation.teamCount
    this.teamSize = this.$store.state.presentation.teamSize
  },
  methods: {
    ...mapActions(['setSyncLoadingAction']),
    ...mapActions('rightPanelContainer', ['toggleQuizTeamSetUpVisibleAction']),

    // eslint-disable-next-line func-names
    onChangeNumberOfTeam: _.debounce(async function () {
      this.notAllowUpdateTeamNumber = false
      if (!this.teamCount) this.teamCount = 2
      if (!this.$store.state.presentation.teamData) this.$store.state.presentation.teamData = []
      if (this.teamCount < 2 || this.teamCount > 500) {
        this.teamCount = this.$store.state.presentation.teamCount
        return
      }

      // Get teams with members
      this.numberOfTeamsWithMembers = this.$store.state.presentation.teamData
        .filter((data) => data.teams && data.teams.length)
        .length

      // Can't change number of teams
      if (this.numberOfTeamsWithMembers > this.teamCount) {
        this.$bvModal.show('team-quiz-reduce-team-modal')
        this.notAllowUpdateTeamNumber = true
        return
      }

      // Can change number of teams
      const teamMap = {}
      this.$store.state.presentation.teamData.forEach((t) => { teamMap[t.id] = t })

      const maxCount = Math.max(this.$store.state.presentation.teamData.length, this.teamCount)
      const activeTeams = this.$store.state.presentation.teamData
        .filter((data) => data.teams && data.teams.length)

      const activeTeamsId = new Set(activeTeams.map((d) => d && d.id))
      const activeTeamsName = new Set(activeTeams.map((d) => d.name && d.name.trim().toLowerCase()))

      for (let i = 1; i <= maxCount; i++) {
        if (!activeTeamsId.has(i)) {
          if (activeTeamsId.size < this.teamCount) {
            // Handle deleted / not
            if (teamMap[i]) {
              teamMap[i].visible = true
              if (!teamMap[i].name) {
                teamMap[i].name = `Team ${i}`
              }
            } else {
              teamMap[i] = {
                id: i,
                name: `Team ${i}`,
                visible: true,
              }
            }

            // Handle duplicated names
            let name = teamMap[i].name.trim().toLowerCase()
            if (activeTeamsName.has(name)) {
              let count = 1
              while (activeTeamsName.has(name)) {
                teamMap[i].name = `Team ${count}`
                name = teamMap[i].name.trim().toLowerCase()
                count += 1
              }
            }

            activeTeamsId.add(i)
            activeTeamsName.add(name)
          } else if (teamMap[i]) {
            teamMap[i].visible = false
          }
        }
      }

      this.$store.state.presentation.teamData = Object.values(teamMap).sort((a, b) => a.id - b.id)
      this.$store.state.presentation.teamCount = this.teamCount
      this.setSyncLoadingAction(1)

      try {
        await PresentationApi.updatePresentationByPresentationId(
          this.$store.state.presentation.id,
          {
            teamCount: this.teamCount,
            teamData: this.$store.state.presentation.teamData,
            uniqueAccessCode: this.$store.state.presentation.uniqueAccessCode,
          },
        )

        this.setSyncLoadingAction(2)
        utils.mixpanelTracking(
          eventCategories.PRESENTATION_EDITOR,
          'Settings menu > Quiz settings > Team Set Up > Change teamCount success',
        )
      } catch (error) {
        this.setSyncLoadingAction(3)

        utils.mixpanelTracking(
          eventCategories.PRESENTATION_EDITOR,
          'Settings menu > Quiz settings > Team Set Up > Change teamCount failed',
          JSON.stringify(error),
        )

        if (error.status) {
          throw error
        }
      }
    }, 1000),

    // eslint-disable-next-line func-names
    onChangeTeamSize: _.debounce(async function () {
      if (!this.teamSize) this.teamSize = 2
      if (this.teamSize < 2 || this.teamSize > this.limit) {
        this.teamSize = this.$store.state.presentation.teamSize
        return
      }
      this.$store.state.presentation.teamSize = this.teamSize
      this.setSyncLoadingAction(1)
      try {
        await PresentationApi.updatePresentationByPresentationId(
          this.$store.state.presentation.id,
          {
            teamSize: this.$store.state.presentation.teamSize,
            uniqueAccessCode: this.$store.state.presentation.uniqueAccessCode,
          },
        )
        this.setSyncLoadingAction(2)
        utils.mixpanelTracking(
          eventCategories.PRESENTATION_EDITOR,
          'Settings menu > Quiz settings > Team Set Up > Change teamSize success',
        )
      } catch (error) {
        this.setSyncLoadingAction(3)
        utils.mixpanelTracking(
          eventCategories.PRESENTATION_EDITOR,
          'Settings menu > Quiz settings > Team Set Up > Change teamSize failed',
          JSON.stringify(error),
        )

        if (error.status) {
          throw error
        }
      }
    }, 1000),

    onChangeScoringRule() {
      EventBus.$emit('SettingsTabContent: update-presentation-quiz-team-configs', {
        teamScoringRule: this.presentation.teamScoringRule,
      })

      utils.mixpanelTracking(
        eventCategories.PRESENTATION_EDITOR,
        'Settings menu > Quiz settings > Team set up > Team scoring rule',
        `${this.presentation.teamScoringRule}`,
      )
    },

    getColor(team) {
      return team.color || utils.getAhaColor(team.id)
    },

    openEditName(item) {
      item.editName = item.name
      item.edit = true
      this.presentation.teamData.push()
      setTimeout(() => {
        document.getElementById('team-name-input').focus()
      }, 1)
    },

    // eslint-disable-next-line func-names
    onChangeTeamName: _.debounce(async function () {
      this.presentation.teamData.push()
    }, 200),

    _handleOutFocusTeamName(item) {
      if (!item.edit) return
      this.changeTeamName(item)
    },

    async changeTeamName(item) {
      const index = this.presentation.teamData.findIndex((e) => {
        return e.id === item.id
      })
      if (this.presentation.teamData[index].loading) return

      // No editName
      if (!this.presentation.teamData[index].editName) {
        this.presentation.teamData[index].edit = false
        this.presentation.teamData[index].error = ''
        this.presentation.teamData.push()
        return
      }

      // editName empty
      if (
        this.presentation.teamData[index].editName &&
          !this.presentation.teamData[index].editName.trim()
      ) {
        this.presentation.teamData[index].error =
          this.$i18nPresenter.t('Please enter team name.')
        this.presentation.teamData.push()
        return
      }

      // editName length > 20
      if (this.presentation.teamData[index].editName.length > 20) {
        this.presentation.teamData[
          index
        ].error = this.$i18nPresenter.t('Team name max length is 20.')
        this.presentation.teamData.push()
        return
      }

      // editName already exists
      const indexTeam = this.presentation.teamData.findIndex(
        (team) => {
          return (
            team.visible === true &&
            team.name.toLowerCase() ===
            this.presentation.teamData[index].editName
              .trim()
              .toLowerCase()
          )
        },
      )

      if (indexTeam !== -1 && indexTeam !== index) {
        this.presentation.teamData[index].error =
          this.$i18nPresenter.t('This team name is already in use, please enter something else.')
        this.presentation.teamData.push()
        return
      }

      this.setSyncLoadingAction(1)
      this.presentation.teamData[
        index
      ].name = item.editName.trim()
      const teamData = this.presentation.teamData.map((team) => {
        delete team.editName
        delete team.error
        delete team.edit
        delete team.loading
        return team
      })
      this.presentation.teamData[index].loading = true
      this.presentation.teamData.push()
      try {
        await PresentationApi.updatePresentationByPresentationId(
          this.presentation.id,
          {
            teamData,
            uniqueAccessCode: this.presentation.uniqueAccessCode,
          },
        )
        delete item.editName
        delete item.error
        delete item.edit
        delete item.loading
        this.setSyncLoadingAction(2)
        this.presentation.teamData.push()

        utils.mixpanelTracking(
          eventCategories.PRESENTATION_EDITOR,
          'Settings menu > Quiz settings > Team Set Up > Quiz team update name success',
        )
      } catch (error) {
        this.setSyncLoadingAction(3)

        this.presentation.teamData.push()
        utils.mixpanelTracking(
          eventCategories.PRESENTATION_EDITOR,
          'Settings menu > Quiz settings > Team Set Up > Quiz team update name failed',
          JSON.stringify(error),
        )

        if (error.status) {
          throw error
        }
      }
    },

    async addNewTeam() {
      const activeTeams = this.presentation.teamData
        .filter((team) => team.visible === true)
      const teamCount = activeTeams.length + 1
      if (teamCount > 500) {
        return
      }

      // Increase team number
      const teamMap = {}
      this.presentation.teamData.forEach((t) => { teamMap[t.id] = t })

      const visible = this.presentation.teamData
        .filter((team) => team.visible === false)
      const activeNames = new Set(activeTeams
        .map((team) => team.name && team.name.trim().toLowerCase()))

      let newTeam

      if (visible.length) {
        [newTeam] = visible
        newTeam.visible = true
      } else {
        const maxId = activeTeams.map((team) => team.id).reduce((a, b) => Math.max(a, b), 0)
        const id = maxId + 1
        newTeam = {
          id,
          name: `Team ${id}`,
          visible: true,
        }
        teamMap[id] = newTeam
      }

      let newName = newTeam.name.trim().toLowerCase()
      if (activeNames.has(newName)) {
        let count = 1
        while (activeNames.has(newName)) {
          newTeam.name = `Team ${count}`
          newName = newTeam.name.trim().toLowerCase()
          count += 1
        }
      }

      this.presentation.teamCount = teamCount
      this.presentation.teamData = Object.values(teamMap)
        .sort((a, b) => a.id - b.id)

      // Set color for each team
      this.presentation.teamData.push()
      this.teams = this.presentation.teamData

      this.setSyncLoadingAction(1)

      // Update presentation
      const payload = {
        teamCount,
        teamData: [],
        uniqueAccessCode: this.presentation.uniqueAccessCode,
      }

      payload.teamData = this.teams.map((te) => {
        return {
          name: te.name,
          id: te.id,
          color: te.color,
          visible: te.visible,
        }
      })
      try {
        const presentationId = this.presentation.id
        await PresentationApi.updatePresentationByPresentationId(presentationId, payload)
        this.setSyncLoadingAction(2)
        utils.mixpanelTracking(eventCategories.PRESENTATION_EDITOR, 'Settings menu > Team Config > Quiz settings > Team Set Up > Add new team success')
      } catch (error) {
        this.setSyncLoadingAction(3)

        utils.mixpanelTracking(eventCategories.PRESENTATION_EDITOR, 'Settings menu > Team Config > Quiz settings > Team Set Up > Add new team fail', JSON.stringify(error))
        throw error
      }
      const noneHasMembers = this.teams.every((item) => {
        return !item.teams
      })
      if (!noneHasMembers) {
        this.presentation.teamActive = this._countTeamActive()
      }
    },

    _countTeamActive() {
      let count = 0
      this.teams.forEach((item) => {
        if (item.visible && item.teams.length) {
          for (let i = 0; i < item.teams.length; i++) {
            if (!item.teams[i].offline) {
              count++
              break
            }
          }
        }
      })
      return count
    },

    onChangeHideIndividualLeaderboard() {
      EventBus.$emit('SettingsTabContent: update-presentation-quiz-team-configs', {
        isHideIndividualLeaderboard: this.presentation.isHideIndividualLeaderboard,
      })
      const payload = {
        socketId: Socket.getSocketId(),
      }
      payload.isHideIndividualLeaderboard = this.presentation.isHideIndividualLeaderboard
      this.$http.put(Config.api.updatePresentation + this.$route.params.id, payload)
      if (this.presentation.isHideIndividualLeaderboard) {
        utils.mixpanelTracking(
          eventCategories.PRESENTATION_EDITOR,
          'Settings menu > Quiz settings > Enable hide individual leaderboard',
        )
      } else {
        utils.mixpanelTracking(
          eventCategories.PRESENTATION_EDITOR,
          'Settings menu > Quiz settings > Disable hide individual leaderboard',
        )
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.play-as-team-setup__header {
  @apply fixed w-full top-0 z-20 flex items-center justify-between p-4;
  @apply shadow-top bg-base-0 border-base-30 text-base-90;
  height: 55px;
}
.item-name {
  @apply min-h-9 border-t border-b border-transparent box-content; // match input height of 38px
}

.item-name:hover .edit-icon {
  @apply inline;
}

.edit-icon {
  @apply hidden;
}

.aha-input-custom {
  @apply appearance-none max-w-lg rounded pl-20;
  @apply py-2 text-sm rounded w-full border border-secondary-20;
  @apply focus:bg-primary-20 focus:border-primary-90 focus:outline-none;
  @apply hover:bg-primary-20 hover:border-primary-90;
  &::placeholder {
    @apply text-base-70 text-sm;
  }
}
.number-of-teams__wrapper {
  display: flex;
  justify-content: space-between;
}
.hide-leaderboard__wrapper {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.25rem;
}
.play-as-team-setup__content-wrapper {
  @apply relative p-4 overflow-y-auto;
  top: 55px;
  height: calc(100% - 55px);
}
</style>
